// Battle economy + production (GDD §4.1/§4.7). Requisition comes from the HQ and
// captured Strategic Points (with soft decay past half the map); Power from base
// generators. Points capture by squad proximity with a visible timer; an outpost must
// be destroyed before its point can flip. Squads reinforce lost models near friendly
// structures (Legion faster). Production runs a per-army queue.

import { dist, type Vec2 } from "../engine/vec";
import { STRUCTURES, TIER2_COST, TIER2_TIME, unit } from "./data";
import type { Army, BuildItem, Faction, StratPoint } from "./types";
import { World } from "./world";

const HQ_REQ = 9; // /sec base requisition from HQ
const POINT_REQ_FACTOR = 0.42; // point.value * this = /sec
const UNDERDOG_REQ = 3; // /sec per point held less than the enemy (comeback lever, GDD §4.1)
const PWR_PER_GEN = 4; // /sec per online generator
const POWER_NODE_PWR = 3; // /sec for holding a power-node point
const CAPTURE_RATE = 0.14; // progress/sec (~7s to flip)
const NEUTRAL_CAPTURE_RATE = 0.22;
const RECLAIM_RATE = 0.3; // /sec to reverse an enemy's partial capture
const AI_INCOME_MULT = 1.0; // battle-AI economy handicap knob (GDD §8.3 difficulty)

export function stepEconomy(world: World, dt: number): void {
  updateCaptures(world, dt);
  updateIncome(world, dt);
  updateReinforcement(world, dt);
  updateProduction(world, dt);

  // Kindled hero passive fervor
  const kh = world.armies.kindled.hero;
  if (kh && !kh.dead && kh.def.aura?.fervorGen) {
    world.armies.kindled.fervor = Math.min(1, world.armies.kindled.fervor + kh.def.aura.fervorGen * dt);
  }
}

// ---- point capture ---------------------------------------------------------

function updateCaptures(world: World, dt: number): void {
  for (const pt of world.points) {
    let legion = 0;
    let kindled = 0;
    for (const sq of world.squads) {
      if (sq.dead) continue;
      for (const m of sq.models) {
        if (!m.alive) continue;
        if (dist(m.pos, pt.pos) <= pt.captureRadius) {
          if (sq.faction === "legion") legion++;
          else kindled++;
        }
      }
    }
    pt.contested = legion > 0 && kindled > 0;
    if (pt.contested) continue; // frozen while contested

    const present: Faction | null = legion > 0 ? "legion" : kindled > 0 ? "kindled" : null;
    if (!present) continue;

    if (pt.owner === present) {
      // reclaim any partial enemy capture
      if (pt.capturing !== present && pt.captureProgress > 0) {
        pt.captureProgress -= RECLAIM_RATE * dt;
        if (pt.captureProgress <= 0) {
          pt.capturing = present;
          pt.captureProgress = 0;
        }
      }
      continue;
    }

    // enemy/neutral point — blocked if a living enemy outpost stands on it
    if (pt.hasOutpost && outpostFaction(world, pt) !== present) continue;

    if (pt.capturing !== present) {
      pt.capturing = present;
      pt.captureProgress = 0;
    }
    const rate = pt.owner === "neutral" ? NEUTRAL_CAPTURE_RATE : CAPTURE_RATE;
    pt.captureProgress += rate * dt;
    if (pt.captureProgress >= 1) {
      pt.captureProgress = 1;
      const prev = pt.owner;
      pt.owner = present;
      world.stats.captures++;
      world.addEffect({ kind: "text", a: { ...pt.pos }, t: 0, ttl: 1.6, text: `${pt.name} taken`, faction: present });
      void prev;
    }
  }
}

function outpostFaction(world: World, pt: StratPoint): Faction | null {
  const idx = world.points.indexOf(pt);
  const s = world.structures.find((st) => st.kind === "outpost" && st.pointIndex === idx && st.hp > 0);
  return s ? s.faction : null;
}

// ---- income ----------------------------------------------------------------

function updateIncome(world: World, dt: number): void {
  const total = world.points.length;
  const half = Math.ceil(total / 2);
  for (const f of ["legion", "kindled"] as Faction[]) {
    const army = world.armies[f];
    if (!world.hqOf(f)) {
      army.reqRate = 0;
      army.pwrRate = 0;
      continue;
    }
    const owned = world.points.filter((p) => p.owner === f).sort((a, b) => b.value - a.value);
    const enemyPts = world.points.filter((p) => p.owner === world.enemyOf(f)).length;
    let req = HQ_REQ;
    let pwr = 0;
    owned.forEach((p, i) => {
      const decay = i < half ? 1 : 0.5; // soft catch-up past half the map
      const outpostBonus = p.hasOutpost && outpostFaction(world, p) === f ? 1.4 : 1;
      req += p.value * POINT_REQ_FACTOR * decay * outpostBonus;
      if (p.isPower) pwr += POWER_NODE_PWR;
    });
    // Underdog catch-up: trailing side earns more so it can rebuild and contest.
    if (owned.length < enemyPts) req += (enemyPts - owned.length) * UNDERDOG_REQ;
    // online generators
    pwr += army.usedGeneratorSlots * PWR_PER_GEN;
    if (army.isAI) {
      req *= AI_INCOME_MULT;
      pwr *= AI_INCOME_MULT;
    }
    army.reqRate = req;
    army.pwrRate = pwr;
    army.req += req * dt;
    army.pwr += pwr * dt;
  }
}

// ---- reinforcement ---------------------------------------------------------

function updateReinforcement(world: World, dt: number): void {
  for (const sq of world.squads) {
    if (sq.dead || sq.def.role === "vehicle" || sq.def.role === "hero") continue;
    if (sq.broken) continue;
    const aliveCount = sq.models.filter((m) => m.alive).length;
    if (aliveCount >= sq.def.modelCount) {
      sq.reinforceTimer = 0;
      continue;
    }
    if (!nearFriendlyStructure(world, sq.faction, sq.center, 360)) {
      sq.reinforceTimer = 0;
      continue;
    }
    const army = world.armies[sq.faction];
    if (army.req < sq.def.reinforceCostPerModel) continue;
    // Legion reinforces faster on ground it holds (GDD §3.1)
    const rate = sq.faction === "legion" ? 0.62 : 1;
    sq.reinforceTimer += dt;
    if (sq.reinforceTimer >= sq.def.reinforceTime * rate) {
      sq.reinforceTimer = 0;
      army.req -= sq.def.reinforceCostPerModel;
      world.reinforceModel(sq);
    }
  }
}

function nearFriendlyStructure(world: World, f: Faction, p: Vec2, r: number): boolean {
  for (const s of world.structures) {
    if (s.faction !== f || s.hp <= 0) continue;
    if (s.kind === "hq" || s.kind === "barracks" || s.kind === "outpost") {
      if (dist(s.pos, p) < r) return true;
    }
  }
  // captured points also serve as rally
  for (const pt of world.points) if (pt.owner === f && dist(pt.pos, p) < r * 0.7) return true;
  return false;
}

// ---- production ------------------------------------------------------------

function updateProduction(world: World, dt: number): void {
  for (const f of ["legion", "kindled"] as Faction[]) {
    const army = world.armies[f];
    if (army.queue.length === 0) continue;
    const item = army.queue[0];
    item.timeLeft -= dt;
    if (item.timeLeft <= 0) {
      completeBuild(world, f, item);
      army.queue.shift();
    }
  }
}

function completeBuild(world: World, f: Faction, item: BuildItem): void {
  const army = world.armies[f];
  const hq = world.hqOf(f);
  const base = hq ? hq.pos : world.map.hq[f];
  switch (item.kind) {
    case "unit": {
      const def = unit(item.id);
      const rally = rallyPoint(world, f);
      const spawn = { x: base.x + (f === "legion" ? 90 : -90), y: base.y + world.rng.range(-40, 40) };
      const sq = world.createSquad(f, item.id, spawn);
      if (def.role === "hero") army.hero = sq;
      // send new squads to the rally
      sq.order = { type: "attackmove", pos: rally, facing: null, targetSquad: -1, targetPoint: -1 };
      break;
    }
    case "structure": {
      const kind = item.id as "barracks" | "generator" | "turret";
      let pos = item.slotPos;
      if (!pos) pos = { x: base.x + (f === "legion" ? 130 : -130), y: base.y - 130 };
      world.spawnStructure(f, kind, pos);
      if (kind === "generator") army.usedGeneratorSlots++;
      break;
    }
    case "tier": {
      army.hqTier = 2;
      if (hq) hq.tier = 2;
      break;
    }
    case "upgrade": {
      const sq = world.squads.find((s) => s.id === item.squadId && !s.dead);
      if (sq) {
        if (item.id === "sergeant") world.applySergeant(sq);
        else world.applyLaunchers(sq);
      }
      break;
    }
  }
}

function rallyPoint(world: World, f: Faction): Vec2 {
  // nearest neutral/enemy point to push toward, else map center
  const enemy = world.enemyOf(f);
  let best: Vec2 | null = null;
  let bd = Infinity;
  const from = world.map.hq[f];
  for (const p of world.points) {
    if (p.owner === f) continue;
    const d = dist(p.pos, from) + (p.owner === enemy ? 200 : 0);
    if (d < bd) {
      bd = d;
      best = p.pos;
    }
  }
  return best ? { ...best } : { x: world.map.width / 2, y: world.map.height / 2 };
}

// ---- commands (called by input + AI) ---------------------------------------

export function canAfford(army: Army, cost: { req: number; pwr: number }): boolean {
  return army.req >= cost.req && army.pwr >= cost.pwr;
}

function queuedSlots(army: Army): number {
  let n = 0;
  for (const it of army.queue) if (it.kind === "unit") n += unit(it.id).slots;
  return n;
}

export function canBuildUnit(world: World, f: Faction, unitId: string): { ok: boolean; reason?: string } {
  const army = world.armies[f];
  const def = unit(unitId);
  if (def.role === "hero" && (army.hero || army.queue.some((q) => q.kind === "unit" && unit(q.id).role === "hero"))) return { ok: false, reason: "hero already fielded" };
  if (def.tier > army.hqTier) return { ok: false, reason: "requires T2 HQ" };
  if (world.slotsUsed(f) + queuedSlots(army) + def.slots > army.slotCap) return { ok: false, reason: "army cap reached" };
  if (!canAfford(army, def.cost)) return { ok: false, reason: "not enough resources" };
  return { ok: true };
}

export function enqueueUnit(world: World, f: Faction, unitId: string): boolean {
  const check = canBuildUnit(world, f, unitId);
  if (!check.ok) return false;
  const army = world.armies[f];
  const def = unit(unitId);
  army.req -= def.cost.req;
  army.pwr -= def.cost.pwr;
  army.queue.push({ kind: "unit", id: unitId, label: def.name, timeLeft: def.buildTime, totalTime: def.buildTime });
  return true;
}

export function enqueueStructure(world: World, f: Faction, kind: "barracks" | "generator" | "turret", slotPos?: Vec2): boolean {
  const army = world.armies[f];
  const s = STRUCTURES[kind];
  if (!canAfford(army, s.cost)) return false;
  if (kind === "generator" && army.usedGeneratorSlots + army.queue.filter((q) => q.kind === "structure" && q.id === "generator").length >= army.generatorSlots.length) return false;
  let pos = slotPos;
  if (kind === "generator" && !pos) {
    const used = army.usedGeneratorSlots + army.queue.filter((q) => q.kind === "structure" && q.id === "generator").length;
    pos = army.generatorSlots[used];
    if (!pos) return false;
  }
  army.req -= s.cost.req;
  army.pwr -= s.cost.pwr;
  army.queue.push({ kind: "structure", id: kind, label: s.name, timeLeft: s.buildTime, totalTime: s.buildTime, slotPos: pos ? { ...pos } : undefined });
  return true;
}

export function enqueueTier(world: World, f: Faction): boolean {
  const army = world.armies[f];
  if (army.hqTier >= 2) return false;
  if (army.queue.some((q) => q.kind === "tier")) return false;
  if (!canAfford(army, TIER2_COST)) return false;
  army.req -= TIER2_COST.req;
  army.pwr -= TIER2_COST.pwr;
  army.queue.push({ kind: "tier", id: "tier2", label: "Advance to T2", timeLeft: TIER2_TIME, totalTime: TIER2_TIME });
  return true;
}

export function enqueueUpgrade(world: World, f: Faction, squadId: number, upgradeId: string): boolean {
  const army = world.armies[f];
  const sq = world.squads.find((s) => s.id === squadId && !s.dead);
  if (!sq) return false;
  const up = sq.def.upgrades?.find((u) => u.id === upgradeId);
  if (!up) return false;
  if (sq.upgrades.includes(upgradeId)) return false;
  if (army.hqTier < 1) return false;
  if (!canAfford(army, up.cost)) return false;
  army.req -= up.cost.req;
  army.pwr -= up.cost.pwr;
  army.queue.push({ kind: "upgrade", id: upgradeId, label: `${sq.def.name}: ${up.name}`, timeLeft: up.buildTime, totalTime: up.buildTime, squadId });
  return true;
}

/** Fortify a captured point with an outpost structure. */
export function fortifyPoint(world: World, f: Faction, pointIndex: number): boolean {
  const pt = world.points[pointIndex];
  if (!pt || pt.owner !== f || pt.hasOutpost) return false;
  const cost = { req: 80, pwr: 0 };
  const army = world.armies[f];
  if (!canAfford(army, cost)) return false;
  army.req -= cost.req;
  const s = world.spawnStructure(f, "outpost", pt.pos);
  s.pointIndex = pointIndex;
  pt.hasOutpost = true;
  return true;
}
